import React, { ChangeEvent } from 'react';
import { useMutation, useQuery } from 'react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../../../components/api';
import { VM_TYPE_MODEL, yupErrorMsg } from '../../../../../components/atoms/const';
import { VMContainerData } from '../../utils/vm-container';
import { sorter } from '../../../../../components/atoms/func';

interface InitialValuesType {
  model: string;
  type: {value: 0 | 1 | 2, label: string} | null;
  selection: string | number;
  start: string;
  configName: string;
  numbering: {value: 0 | 1 | 2, label: string } | null;
  trays: VMContainerData[];
}

function useAddModelConfiguration() {
  // Get id from params when add model to config
  const { modelId } = useParams();

  // Navigation Handler
  const navigation = useNavigate();
  const navigateToLanding = () => navigation('/vending-machine/model-management');
  const navigateToModelDetail = () => navigation(`/vending-machine/model-management/${modelId}`);

  /*
  *
  *
  *
  *
  * Form handler to Add New Model or Add Model to config
  * */
  // 1. Add New Model and Config
  const { mutate: addNewModel, isLoading: addNewModelLoad } = useMutation(api.vendingMachine.addNewModel, {
    onMutate: (variables) => console.log(variables),
    onSuccess: (data) => {
      console.log(data);
      alert('success');
      navigateToLanding();
    },
    onError: (error) => {
      console.log(error);
      alert('error');
    },
  });
  // 2. Add New Config to Model
  const { mutate: addNewConfig, isLoading: addNewConfigLoad } = useMutation(api.vendingMachine.addConfigToModel, {
    onMutate: (variables) => console.log(variables),
    onSuccess: (data) => {
      console.log(data);
      alert('success');
      navigateToModelDetail();
    },
    onError: (error) => {
      console.log(error);
      alert('error');
    },
  });
  // Form Handler
  const yupSchema = yup.object({
    model: yup.string().required(yupErrorMsg.MODEL_NAME_EMPTY),
    type: yup.object().nullable().required(yupErrorMsg.MACHINE_TYPE_EMPTY),
    configName: yup.string().required(yupErrorMsg.CONFIG_NAME_EMPTY),
    trays: yup.array().min(1, yupErrorMsg.MODEL_TRAY_EMPTY),
  });
  const initValues: InitialValuesType = {
    model: '',
    selection: '',
    start: '',
    numbering: null,
    trays: [],
    configName: '',
    type: null,
  };
  const formik = useFormik({
    initialValues: initValues,
    validationSchema: yupSchema,
    onSubmit: (values) => {
      if (values.trays) {
        if (modelId) {
          addNewConfig({
            id: Number(modelId),
            configName: values.configName,
            trays: values.trays.map((x) => x.id),
          });
        } else {
          addNewModel({
            model: values.model,
            type: values.type!.value,
            trays: values.trays.map((x) => x.id),
            configName: values.configName,
          });
        }
      }
    },
  });
  const {
    selection, start, numbering, trays,
  } = formik.values;

  /*
  *
  *
  *
  *
  * Tray list chooser handler
  * Pagination when data not filtered, because it have a chance to make screen
  * get freeze when total data more than 30
  * */
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const { data: traysRes, isLoading: traysLoad } = useQuery(
    (selection.toString() && selection.toString() !== '')
      ? ['findAllTray', 'vendingMachine', selection, start, numbering?.value]
      : ['getAllTray', 'vendingMachine', (currentPage - 1).toString(), '10'],
    () => {
      if (selection.toString() && selection.toString() !== '') {
        const conditionNumbering = !!start.toString() && !!selection.toString();
        return api.vendingMachine.findAllTray(
          selection?.toString() ?? '',
          selection.toString() ? start?.toString() : '',
          conditionNumbering ? numbering?.value?.toString() : '',
        );
      }
      return api.vendingMachine.getAllTray((currentPage - 1).toString(), '9');
    },
  );
  const trayList = React.useMemo(() => ({
    totalPage: traysRes?.data?.totalPages ?? 1,
    data: traysRes?.data?.data,
  }), [traysRes]);

  /*
  *
  *
  *
  *
  * Form Handler when tray check button is triggered by change from user
  * */
  const handleChangeTray = (e: ChangeEvent<HTMLInputElement>, data: VMContainerData) => {
    if (!e.target.checked) {
      const removed = trays.filter((x) => x.id !== data.id);
      formik.setFieldValue('trays', removed);
    } else {
      formik.setFieldValue('trays', [...trays, data]);
    }
  };

  // Showing Data Handler on Vending Machine Visualization
  const sortedTray = React.useMemo(
    () => trays?.sort((a, b) => sorter(a.start, b.start)),
    [trays],
  );
  // Keep Current Page to 1 whenever selection is reset by user
  React.useEffect(() => {
    if (selection === '') setCurrentPage(1);
  }, [selection]);

  /*
  *
  *
  *
  *
  * Add Config to Existing Model initial data auto fill
  * */
  // Get All Model to get Model Name and Id for UI and Config List handler later
  const { data: modelRes, isLoading: modelLoad } = useQuery(
    ['getAllModel', 'vendingMachine'],
    () => api.vendingMachine.getAllModel(),
    { enabled: !!modelId },
  );
  const findModel = React.useMemo(() => {
    const resData = modelRes?.data?.data;
    if (resData && modelId) {
      return resData.find((x) => x.id.toString() === modelId);
    }
    return undefined;
  }, [modelRes, modelId]);
  React.useEffect(() => {
    if (findModel) {
      formik.setFieldValue('model', findModel.model ?? '');
      const findType = findModel.type > 2 ? 0 : findModel.type;
      formik.setFieldValue('type', VM_TYPE_MODEL.find((x) => x.value === findType));
    }
  }, [findModel]);

  return {
    tray: {
      list: trayList.data,
      load: traysLoad,
      handler: handleChangeTray,
      sorted: sortedTray,
      totalPage: trayList.totalPage,
    },
    formik,
    page: {
      current: currentPage,
      setCurrent: setCurrentPage,
    },
    addLoad: addNewModelLoad || addNewConfigLoad,
    model: {
      id: modelId,
      load: modelLoad,
      data: findModel,
    },
    navigation: {
      detail: navigateToModelDetail,
      landing: navigateToLanding,
    },
  };
}

export default useAddModelConfiguration;
